import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// 일괄 선택 기간 (달력 기준 일수)
const RANGE_PRESETS = [7, 14, 28];

// 로컬 기준 YYYY-MM-DD. toISOString은 UTC로 밀려 날짜가 하루 어긋난다
const toDateString = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * 학습 날짜 선택기 — 시험 출제와 단어장이 함께 쓴다.
 *
 * 단어장이 쌓이면 날짜를 평면 나열할 수 없어 월별로 접고,
 * 기간 프리셋으로 여러 날을 한 번에 고를 수 있게 한다.
 *
 * @param {{study_date: string, word_count: number}[]} dates 날짜 목록 (최신순으로 와도 무방)
 * @param {string[]} selected 선택된 날짜
 * @param {(next: string[]) => void} onChange
 * @param {React.ReactNode} summaryExtra 선택 요약 옆에 덧붙일 내용 (중복 안내 등)
 * @param {number} [wordCount] 요약에 쓸 단어 수. 시험은 중복을 뺀 실제 출제 수를 쓰므로
 *                             날짜별 합계와 다르다. 주지 않으면 합계를 쓴다
 */
const StudyDatePicker = ({ dates, selected, onChange, summaryExtra = null, wordCount }) => {
  const { t } = useTranslation();
  // 0이면 개별 선택, 그 외에는 클릭한 날부터 해당 일수만큼 한 번에 선택한다
  const [rangeDays, setRangeDays] = useState(0);
  const [openMonths, setOpenMonths] = useState([]);
  const monthsInitialized = useRef(false);

  // 목록은 최신순으로 오지만, 고르는 것은 배운 순서대로 훑는 편이 자연스럽다
  const pickerDates = useMemo(
    () => [...dates].sort((a, b) => a.study_date.localeCompare(b.study_date)),
    [dates]
  );

  // 날짜가 쌓여도 훑을 수 있도록 월별로 묶는다
  const monthGroups = useMemo(() => {
    const map = new Map();
    for (const d of pickerDates) {
      const month = d.study_date.slice(0, 7);
      if (!map.has(month)) map.set(month, { month, days: [], words: 0 });
      const g = map.get(month);
      g.days.push(d);
      g.words += d.word_count;
    }
    return Array.from(map.values());
  }, [pickerDates]);

  // 처음 목록을 받으면 가장 최근 달만 펼쳐 둔다.
  // 매번 다시 계산하면 사용자가 접어 둔 달이 되살아난다
  useEffect(() => {
    if (monthsInitialized.current || monthGroups.length === 0) return;
    monthsInitialized.current = true;
    setOpenMonths([monthGroups[monthGroups.length - 1].month]);
  }, [monthGroups]);

  const toggleMonth = (month) => {
    setOpenMonths(prev =>
      prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
    );
  };

  /** 이미 다 선택돼 있으면 해제하고, 아니면 채운다 (잘못 누른 범위를 되돌릴 수 있어야 한다) */
  const toggleGroup = (groupDates) => {
    const allPicked = groupDates.length > 0 && groupDates.every(d => selected.includes(d));
    onChange(allPicked
      ? selected.filter(d => !groupDates.includes(d))
      : Array.from(new Set([...selected, ...groupDates])));
  };

  /**
   * 클릭한 날짜부터 rangeDays일간(달력 기준)에 해당하는 학습 날짜들.
   * 공부하지 않은 날은 애초에 목록에 없으므로 자연히 걸러진다.
   */
  const datesInRange = (startDate) => {
    const end = new Date(`${startDate}T00:00:00`);
    end.setDate(end.getDate() + rangeDays - 1);
    const endDate = toDateString(end);
    return pickerDates
      .filter(d => d.study_date >= startDate && d.study_date <= endDate)
      .map(d => d.study_date);
  };

  const handleDateClick = (date) => {
    if (rangeDays === 0) {
      onChange(selected.includes(date)
        ? selected.filter(d => d !== date)
        : [...selected, date]);
      return;
    }
    toggleGroup(datesInRange(date));
  };

  const summaryWordCount = wordCount !== undefined
    ? wordCount
    : dates.filter(d => selected.includes(d.study_date))
      .reduce((sum, d) => sum + d.word_count, 0);

  return (
    <div className="quiz-field">
      <label>{t('vocabulary.quiz.pickDates')}</label>

      {/* 기간을 고른 뒤 날짜를 클릭하면 그 날부터 해당 기간이 한 번에 잡힌다 */}
      <div className="quiz-range-modes">
        <button
          className={`quiz-range-btn${rangeDays === 0 ? ' active' : ''}`}
          onClick={() => setRangeDays(0)}
        >
          {t('vocabulary.quiz.pickOneByOne')}
        </button>
        {RANGE_PRESETS.map(days => (
          <button
            key={days}
            className={`quiz-range-btn${rangeDays === days ? ' active' : ''}`}
            onClick={() => setRangeDays(days)}
          >
            {t('vocabulary.quiz.rangeDays', { days })}
          </button>
        ))}
      </div>
      <p className="quiz-hint">
        {rangeDays === 0
          ? t('vocabulary.quiz.pickOneByOneHint')
          : t('vocabulary.quiz.rangeHint', { days: rangeDays })}
      </p>

      <div className="quiz-month-list">
        {monthGroups.length === 0 && <span className="vocab-empty">{t('vocabulary.noWords')}</span>}
        {monthGroups.map(g => {
          const monthDates = g.days.map(d => d.study_date);
          const picked = monthDates.filter(d => selected.includes(d)).length;
          const open = openMonths.includes(g.month);
          return (
            <div className="quiz-month" key={g.month}>
              <div className={`quiz-month-head${open ? ' open' : ''}`}>
                <button className="quiz-month-toggle" onClick={() => toggleMonth(g.month)}>
                  <span className="quiz-month-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
                  <span className="quiz-month-label">{g.month}</span>
                  <span className="quiz-month-count">
                    {t('vocabulary.monthSummary', { days: g.days.length, words: g.words })}
                  </span>
                  {picked > 0 && (
                    <span className="quiz-month-picked">
                      {t('vocabulary.quiz.monthPicked', { count: picked })}
                    </span>
                  )}
                </button>
                <button className="quiz-month-all" onClick={() => toggleGroup(monthDates)}>
                  {picked === g.days.length
                    ? t('vocabulary.quiz.clearMonth')
                    : t('vocabulary.quiz.selectMonth')}
                </button>
              </div>

              {open && (
                <div className="quiz-date-picker">
                  {g.days.map(d => (
                    <button
                      key={d.study_date}
                      className={`quiz-date-chip${selected.includes(d.study_date) ? ' selected' : ''}`}
                      onClick={() => handleDateClick(d.study_date)}
                      title={d.alias && d.alias !== d.study_date ? `${d.alias} (${d.study_date})` : d.study_date}
                    >
                      {/* 이름을 붙여 올린 날짜는 날짜 대신 그 이름으로 고른다 */}
                      {d.alias && d.alias !== d.study_date ? d.alias : d.study_date.slice(5)}
                      {' '}<em>{d.word_count}</em>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selected.length > 0 && (
        <p className="quiz-hint quiz-selected-row">
          <span>
            {t('vocabulary.quiz.selectedSummary', {
              dates: selected.length, words: summaryWordCount,
            })}
            {summaryExtra}
          </span>
          <button className="quiz-clear-btn" onClick={() => onChange([])}>
            {t('vocabulary.quiz.clearDates')}
          </button>
        </p>
      )}
    </div>
  );
};

export default StudyDatePicker;
